import React, { useState, useEffect } from "react";
import { CampaignData, CampaignContent } from "../../../types";
import { SingleCampaignResponse } from "../../../../views/marketing-pages/campaign/types";
import { TemplateModule } from "../../../../views/marketing-pages/templates";
import useRequest from "../../../hook/use-req";
import { Plus } from "lucide-react";
import { TemplateCreator } from "../../templates/create-template";
import { RcsPreview } from "../../templates/rcs/rcs-preview";
import { RcsComponent } from "../../templates/rcs/rcs-types";

// ─────────────────────────────────────────────────────────────
// VARIABLE MAPPING TYPES & HELPERS (same as WhatsApp)
// ─────────────────────────────────────────────────────────────

type VariableSource = "csv" | "static";

interface VariableMapping {
  variableName: string;
  source: VariableSource;
  csvColumn?: string;
  staticValue?: string;
}

// Extract variables from RCS template components (text, rich_card title/description, etc.)
function extractRcsVariables(components: RcsComponent[]): string[] {
  const variables = new Set<string>();

  components.forEach((component) => {
    // Check text content
    if (component.type === "text" && component.content) {
      const matches = component.content.matchAll(/{{(\w+)}}/g);
      for (const match of matches) {
        variables.add(match[1]);
      }
    }

    // Check rich card title and description
    if (component.type === "rich_card") {
      if (component.title) {
        const titleMatches = component.title.matchAll(/{{(\w+)}}/g);
        for (const match of titleMatches) {
          variables.add(match[1]);
        }
      }
      if (component.description) {
        const descMatches = component.description.matchAll(/{{(\w+)}}/g);
        for (const match of descMatches) {
          variables.add(match[1]);
        }
      }
    }

    // Check carousel cards
    if (component.type === "carousel" && component.cards) {
      component.cards.forEach((card: any) => {
        if (card.title) {
          const titleMatches = card.title.matchAll(/{{(\w+)}}/g);
          for (const match of titleMatches) {
            variables.add(match[1]);
          }
        }
        if (card.description) {
          const descMatches = card.description.matchAll(/{{(\w+)}}/g);
          for (const match of descMatches) {
            variables.add(match[1]);
          }
        }
      });
    }

    // Check suggested replies text
    if (component.type === "suggested_replies" && component.content) {
      const matches = component.content.matchAll(/{{(\w+)}}/g);
      for (const match of matches) {
        variables.add(match[1]);
      }
    }

    // Check CTA text
    if (component.type === "cta_buttons" && component.ctaText) {
      const matches = component.ctaText.matchAll(/{{(\w+)}}/g);
      for (const match of matches) {
        variables.add(match[1]);
      }
    }
  });

  return [...variables];
}

function buildInitialMappings(
  variableNames: string[],
  csvColumns: string[],
  existingColumnMap?: Record<string, string>,
  existingStatic?: Record<string, string>
): VariableMapping[] {
  return variableNames.map((name) => {
    const cleanName = name.replace(/[{}]/g, "");

    if (existingColumnMap?.[cleanName] || existingColumnMap?.[name]) {
      return {
        variableName: cleanName,
        source: "csv",
        csvColumn: existingColumnMap[cleanName] || existingColumnMap[name]
      };
    }
    if (existingStatic?.[cleanName] || existingStatic?.[name]) {
      return {
        variableName: cleanName,
        source: "static",
        staticValue: existingStatic[cleanName] || existingStatic[name]
      };
    }

    // Auto-match variable name with CSV column (case-insensitive)
    const autoMatch = csvColumns.find((col) =>
      col.toLowerCase() === cleanName.toLowerCase() ||
      col.toLowerCase().replace(/[_\s]/g, "") === cleanName.toLowerCase().replace(/[_\s]/g, "")
    );

    if (autoMatch && csvColumns.length > 0) {
      return { variableName: cleanName, source: "csv", csvColumn: autoMatch };
    }

    return { variableName: cleanName, source: "static", staticValue: "" };
  });
}

function mappingsToPayload(mappings: VariableMapping[]): {
  variable_column_map: Record<string, string>;
  variables: Record<string, string>;
  data_to_personalize: string[];
} {
  const variable_column_map: Record<string, string> = {};
  const variables: Record<string, string> = {};
  const data_to_personalize: string[] = [];

  mappings.forEach((m) => {
    if (m.source === "csv" && m.csvColumn) {
      variable_column_map[m.variableName] = m.csvColumn;
      data_to_personalize.push(m.variableName);
    } else {
      variables[m.variableName] = m.staticValue || "";
    }
  });

  return { variable_column_map, variables, data_to_personalize };
}

interface RcsContentCreationProps {
  campaignData: CampaignData;
  singleCampaign: SingleCampaignResponse | null;
  setCampaignData: React.Dispatch<React.SetStateAction<CampaignData>>;
  onSubmit: (data: Partial<CampaignData>) => void;
  onValidation: (isValid: boolean) => void;
  onBack: () => void;
}

interface RcsTemplate {
  id: string;
  name: string;
  content: string;
  components: RcsComponent[];
  use_case: string;
  language: string;
  category: string;
  status: string;
}

function apiActionToButton(action: any): import("../../templates/rcs/rcs-types").RcsButton {
  const actionTypeMap: Record<string, 'open_url' | 'dial' | 'location' | 'calendar'> = {
    URL: 'open_url',
    PHONE_NUMBER: 'dial',
    LOCATION: 'location',
    CALENDAR: 'calendar',
  };

  return {
    id: `btn_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    text: action.text || '',
    action: actionTypeMap[action.type] || 'dial',
    value: action.url || action.phone_number || '',
  };
}

function convertApiComponentToInternal(apiComponent: any): RcsComponent {
  const id = apiComponent.id || `${apiComponent.type}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  switch (apiComponent.type) {
    case 'text':
      return {
        id,
        type: 'text',
        content: apiComponent.text || '',
      };

    case 'image':
      return {
        id,
        type: 'image',
        mediaUrl: apiComponent.media_url || '',
        mediaCaption: apiComponent.caption || '',
      };

    case 'video':
      return {
        id,
        type: 'video',
        mediaUrl: apiComponent.media_url || '',
        mediaCaption: apiComponent.caption || '',
      };

    case 'rich_card':
      return {
        id,
        type: 'rich_card',
        title: apiComponent.title || '',
        description: apiComponent.description || '',
        mediaUrl: apiComponent.media_url || '',
        orientation: apiComponent.orientation || 'VERTICAL',
        mediaHeight: apiComponent.media_height || 'MEDIUM',
        buttons: (apiComponent.actions || []).map(apiActionToButton),
      };

    case 'carousel':
      return {
        id,
        type: 'carousel',
        carouselText: apiComponent.text || '',
        cardWidth: apiComponent.card_width || 'MEDIUM',
        cards: (apiComponent.cards || []).map((card: any) => ({
          id: card.id || `card_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          title: card.title || '',
          description: card.description || '',
          mediaUrl: card.media_url || '',
          mediaHeight: card.media_height || 'MEDIUM',
          buttons: (card.actions || []).map(apiActionToButton),
        })),
      };

    case 'suggested_replies':
      return {
        id,
        type: 'suggested_replies',
        content: apiComponent.text || '',
        suggestedReplies: (apiComponent.replies || []).map((reply: any) => ({
          id: `reply_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          text: reply.text || '',
          postbackData: reply.postback_data || '',
        })),
        replies: (apiComponent.replies || []).map((reply: any) => reply.text || ''),
      };

    case 'call_to_action':
      return {
        id,
        type: 'cta_buttons',
        ctaText: apiComponent.text || '',
        buttons: (apiComponent.actions || []).map(apiActionToButton),
      };

    case 'location':
      return {
        id,
        type: 'location',
        locationText: apiComponent.text || '',
        locationLabel: apiComponent.label || '',
        latitude: apiComponent.latitude,
        longitude: apiComponent.longitude,
      };

    case 'divider':
      return {
        id,
        type: 'divider',
      };

    default:
      return {
        id,
        type: apiComponent.type as any,
        content: apiComponent.text || '',
      };
  }
}

function convertToRcsTemplate(backendTemplate: TemplateModule): RcsTemplate {
  const apiComponents = (backendTemplate as any).components || [];
  const components: RcsComponent[] = apiComponents.map(convertApiComponentToInternal);

  return {
    id: backendTemplate.id,
    name: backendTemplate.template_name,
    content: backendTemplate.content,
    components,
    use_case: (backendTemplate as any).use_case || "Order Update",
    language: backendTemplate.language,
    category: backendTemplate.category,
    status: backendTemplate.wa_status,
  };
}

function TemplateListItem({
  template,
  selected,
  onSelect,
}: {
  template: RcsTemplate;
  selected: boolean;
  onSelect: (t: RcsTemplate) => void;
}) {
  const getStatusBadge = (status: string) => {
    switch (status?.toUpperCase()) {
      case "APPROVED":
        return "bg-green-100 dark:bg-green-500/15 text-green-700 dark:text-green-400";
      case "PENDING":
        return "bg-yellow-100 dark:bg-yellow-500/15 text-yellow-700 dark:text-yellow-400";
      case "REJECTED":
        return "bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-400";
      default:
        return "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300";
    }
  };

  const componentCount = template.components?.length || 0;

  return (
    <button
      onClick={() => onSelect(template)}
      className={`w-full text-left px-4 py-3 border-b border-slate-100 dark:border-slate-800 transition-colors hover:bg-slate-50 dark:hover:bg-slate-800
        ${selected ? "bg-blue-50 dark:bg-blue-500/10 border-l-4 border-l-blue-500" : "border-l-4 border-l-transparent"}`}
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="font-semibold text-slate-800 dark:text-slate-200 text-sm truncate">{template.name}</span>
        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${getStatusBadge(template.status)}`}>
          {template.status}
        </span>
      </div>
      <p className="text-xs text-slate-400 dark:text-slate-500 truncate leading-relaxed">
        {componentCount} component{componentCount !== 1 ? "s" : ""} • {template.use_case}
      </p>
    </button>
  );
}

function TemplateSidebar({
  templates,
  selected,
  onSelect,
  loading,
}: {
  templates: RcsTemplate[];
  selected: RcsTemplate | null;
  onSelect: (t: RcsTemplate) => void;
  loading?: boolean;
}) {
  const [query, setQuery] = useState("");
  const filtered = templates.filter((t) => t.name.toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="flex flex-col h-full border-r border-slate-200 dark:border-slate-700">
      <div className="px-4 pt-5 pb-3">
        <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">Choose RCS Template</h1>
        <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">Pick a template or create new</p>
      </div>
      <div className="px-4 bg-blue-50 dark:bg-blue-500/10 p-3">
        <div className="relative">
          <input
            type="text"
            placeholder="Search Templates"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full border border-slate-200 dark:border-slate-700 rounded-lg pl-3 pr-9 py-2 text-sm placeholder:text-slate-400 dark:placeholder:text-slate-500 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-300 bg-white dark:bg-gray-900"
          />
          <svg className="absolute right-3 top-2.5 text-slate-400 dark:text-slate-500 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
          </svg>
        </div>
      </div>

      <div className="flex-1 bg-white dark:bg-gray-900 overflow-y-auto" style={{ maxHeight: "650px", minHeight: "300px" }}>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : filtered.length === 0 ? (
          <p className="text-center text-slate-400 dark:text-slate-500 text-sm py-8">
            {templates.length === 0 ? "No RCS templates found" : "No matching templates"}
          </p>
        ) : (
          filtered.map((t) => (
            <TemplateListItem key={t.id} template={t} selected={selected?.id === t.id} onSelect={onSelect} />
          ))
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Variable Mapping Form Component
// ─────────────────────────────────────────────────────────────

interface VariableMappingFormProps {
  variableNames: string[];
  mappings: VariableMapping[];
  onChange: (mappings: VariableMapping[]) => void;
}

function VariableMappingForm({
  variableNames,
  mappings,
  onChange,
}: VariableMappingFormProps) {
  if (variableNames.length === 0) return null;

  // Get headers from localStorage (set during CSV upload in step 2)
  const savedHeadersStr = localStorage.getItem("csv_headers");
  const csvColumns: string[] = savedHeadersStr ? JSON.parse(savedHeadersStr) : [];
  const savedHeadersColumn = localStorage.getItem("first_row_values");
  const csvColumnsRow: (string | number | null)[] = savedHeadersColumn ? JSON.parse(savedHeadersColumn) : [];
  const csvHeadersName = localStorage.getItem("metadata");
  const metadata: { unique_file_name: string } | null = csvHeadersName ? JSON.parse(csvHeadersName) : null;

  const mappedCount = mappings.filter(
    (m) => (m.source === "csv" && m.csvColumn) || (m.source === "static" && m.staticValue)
  ).length;

  const updateMapping = (index: number, patch: Partial<VariableMapping>) => {
    onChange(mappings.map((m, i) => (i === index ? { ...m, ...patch } : m)));
  };

  const getPreview = (m: VariableMapping): string => {
    if (m.source === "csv" && m.csvColumn) {
      const colIndex = csvColumns.indexOf(m.csvColumn);
      const rowValue = colIndex !== -1 ? csvColumnsRow[colIndex] : undefined;
      return rowValue !== undefined && rowValue !== null ? String(rowValue) : m.csvColumn;
    }
    return m.staticValue || "";
  };

  return (
    <div className="border-t border-slate-200 dark:border-slate-700 mt-4 pt-4">
      {metadata?.unique_file_name && (
        <p className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 mb-2">{metadata.unique_file_name}</p>
      )}
      <div className="flex justify-between items-center mb-3">
        <p className="text-sm font-semibold text-slate-800 dark:text-slate-200">Personalize Variables</p>
        <span className="text-xs font-medium px-2 py-0.5 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full">
          {mappedCount}/{variableNames.length} Mapped
        </span>
      </div>

      <div className="bg-white dark:bg-gray-900 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
        <div className="grid grid-cols-[100px_1fr_100px] gap-3 px-4 py-2 bg-slate-50 dark:bg-slate-800 border-b text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
          <div>Variable</div>
          <div>Data Source</div>
          <div>Preview</div>
        </div>

        {mappings.map((m, idx) => (
          <div key={m.variableName} className="grid grid-cols-[100px_1fr_100px] gap-3 px-4 py-3 border-b last:border-b-0 items-center">
            {/* Variable Name */}
            <div className="font-mono text-xs text-indigo-600 dark:text-indigo-400">
              {`{{${m.variableName}}}`}
            </div>

            {/* Data Source */}
            <div className="space-y-2">
              <div className="flex gap-4">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    checked={m.source === "csv"}
                    onChange={() => updateMapping(idx, { source: "csv", staticValue: undefined })}
                    className="accent-indigo-600 dark:accent-indigo-500 w-3 h-3"
                  />
                  <span className="text-xs">CSV Column</span>
                </label>

                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="radio"
                    checked={m.source === "static"}
                    onChange={() => updateMapping(idx, { source: "static", csvColumn: undefined })}
                    className="accent-indigo-600 dark:accent-indigo-500 w-3 h-3"
                  />
                  <span className="text-xs">Default Value</span>
                </label>
              </div>

              {m.source === "csv" ? (
                <select
                  value={m.csvColumn || ""}
                  onChange={(e) => updateMapping(idx, { csvColumn: e.target.value })}
                  className="w-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-transparent text-slate-900 dark:text-slate-100 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-200"
                >
                  <option value="">Select column...</option>
                  {csvColumns.map((col) => (
                    <option key={col} value={col}>{col}</option>
                  ))}
                </select>
              ) : (
                <input
                  value={m.staticValue || ""}
                  onChange={(e) => updateMapping(idx, { staticValue: e.target.value })}
                  placeholder="Enter default value"
                  className="w-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-transparent text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-500 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-200"
                />
              )}
            </div>

            {/* Preview */}
            <div>
              {getPreview(m) ? (
                <span className="inline-block bg-green-50 dark:bg-green-500/15 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800 px-2 py-1 rounded text-xs truncate max-w-full">
                  {getPreview(m)}
                </span>
              ) : (
                <span className="text-slate-300 dark:text-slate-600 italic text-xs">—</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export const RcsContentCreation: React.FC<RcsContentCreationProps> = ({
  campaignData,
  singleCampaign,
  setCampaignData,
  onSubmit,
  onValidation,
  onBack,
}) => {
  const token = localStorage.getItem("authToken");
  const [templates, setTemplates] = useState<RcsTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<RcsTemplate | null>(null);
  const [loading, setLoading] = useState(true);
  const [showTemplateCreator, setShowTemplateCreator] = useState(false);

  // Variable mapping state
  const [variableNames, setVariableNames] = useState<string[]>([]);
  const [variableMappings, setVariableMappings] = useState<VariableMapping[]>([]);

  const { makeRequest: getTemplates } = useRequest("/templates/", "GET", {
    Authorization: `Bearer ${token}`,
  });

  useEffect(() => {
    const fetchTemplates = async () => {
      setLoading(true);
      try {
        const [response] = await getTemplates(undefined, { channel: "RCS" });
        // Disabled templates can't be used in a campaign.
        const raw = (response?.data ?? []).filter((t: any) => t?.is_active !== false);
        const converted = raw.map(convertToRcsTemplate);
        setTemplates(converted);

        const campaignTemplateId = (singleCampaign as any)?.template_id;
        if (campaignTemplateId) {
          const existing = converted.find((t: RcsTemplate) => t.id === campaignTemplateId);
          if (existing) setSelectedTemplate(existing);
        } else if (converted.length > 0) {
          setSelectedTemplate(converted[0]);
        }
      } catch (error) {
        console.error("Failed to fetch RCS templates:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchTemplates();
  }, []);

  // Extract variables when template changes
  useEffect(() => {
    if (!selectedTemplate) {
      setVariableNames([]);
      setVariableMappings([]);
      onValidation(false);
      return;
    }

    // Extract variables from RCS components
    const extractedVars = extractRcsVariables(selectedTemplate.components || []);
    setVariableNames(extractedVars);

    // Get CSV columns from localStorage
    const savedHeadersStr = localStorage.getItem("csv_headers");
    const csvColumns: string[] = savedHeadersStr ? JSON.parse(savedHeadersStr) : [];

    // Get existing mappings from campaign data if editing
    const existingColumnMap = (singleCampaign as any)?.variable_column_map || campaignData.content?.variable_column_map;
    const existingStatic = (singleCampaign as any)?.variables || campaignData.content?.variables;

    // Build initial mappings
    const initialMappings = buildInitialMappings(extractedVars, csvColumns, existingColumnMap, existingStatic);
    setVariableMappings(initialMappings);

    onValidation(true);
  }, [selectedTemplate]);

  // Update campaign data when mappings change
  useEffect(() => {
    if (!selectedTemplate) return;

    const { variable_column_map, variables, data_to_personalize } = mappingsToPayload(variableMappings);

    const content: CampaignContent = {
      template_id: selectedTemplate.id,
      template_name: selectedTemplate.name,
      variables,
      variable_column_map,
      data_to_personalize,
      personalize: data_to_personalize.length > 0,
      header_type: "none",
      header_value: undefined,
      message: selectedTemplate.content,
      footer: "",
      buttons: [],
    };

    setCampaignData((prev) => ({
      ...prev,
      content,
      messages: selectedTemplate.content,
      rcsComponents: selectedTemplate.components,
    }));
  }, [selectedTemplate, variableMappings]);

  const handleTemplateSelect = (template: RcsTemplate) => {
    setSelectedTemplate(template);
  };

  const handleContinue = () => {
    if (selectedTemplate) {
      const { variable_column_map, variables, data_to_personalize } = mappingsToPayload(variableMappings);

      onSubmit({
        content: {
          template_id: selectedTemplate.id,
          template_name: selectedTemplate.name,
          variables,
          variable_column_map,
          data_to_personalize,
          personalize: data_to_personalize.length > 0,
          header_type: "none",
          header_value: undefined,
          message: selectedTemplate.content,
          footer: "",
          buttons: [],
        },
        messages: selectedTemplate.content,
      });
    }
  };

  return (
    <div className="flex flex-col lg:flex-row h-full bg-white dark:bg-gray-900 rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700">
      <div className="w-full lg:w-80 lg:flex-shrink-0">
        <TemplateSidebar
          templates={templates}
          selected={selectedTemplate}
          onSelect={handleTemplateSelect}
          loading={loading}
        />
      </div>

      <div className="flex-1 min-w-0 flex flex-col">
        <div className="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-200">
              {selectedTemplate ? selectedTemplate.name : "Select a Template"}
            </h2>
            <p className="text-xs text-slate-400 dark:text-slate-500">
              {selectedTemplate
                ? `${selectedTemplate.components?.length || 0} components • ${selectedTemplate.use_case}`
                : "Choose an RCS template from the sidebar"}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowTemplateCreator(true)}
            className="flex items-center gap-1.5 px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors bg-white dark:bg-gray-900 shadow-sm"
          >
            <Plus className="w-4 h-4" />
            Create New Template
          </button>
        </div>

        <div className="flex-1 flex flex-col md:flex-row overflow-y-auto md:overflow-hidden">
          {/* Preview Panel */}
          <div className="w-full md:w-1/2 flex items-center justify-center p-6 bg-slate-50 dark:bg-slate-800 border-b md:border-b-0 md:border-r border-slate-200 dark:border-slate-700">
            {selectedTemplate ? (
              <RcsPreview
                components={selectedTemplate.components}
                senderName={campaignData.sender_name || "Your Business"}
              />
            ) : (
              <div className="text-center text-slate-400 dark:text-slate-500">
                <div className="w-16 h-16 mx-auto mb-4 bg-slate-200 dark:bg-slate-700 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-slate-400 dark:text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                </div>
                <p className="text-sm font-medium">No template selected</p>
                <p className="text-xs mt-1">Select a template from the sidebar to preview</p>
              </div>
            )}
          </div>

          {/* Variable Mapping Panel */}
          <div className="w-full md:w-1/2 p-6 overflow-y-auto bg-white dark:bg-gray-900">
            {selectedTemplate && variableNames.length > 0 ? (
              <VariableMappingForm
                variableNames={variableNames}
                mappings={variableMappings}
                onChange={setVariableMappings}
              />
            ) : selectedTemplate ? (
              <div className="h-full flex items-center justify-center text-center text-slate-400 dark:text-slate-500">
                <div>
                  <div className="w-12 h-12 mx-auto mb-3 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-slate-400 dark:text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <p className="text-sm font-medium">No Variables Found</p>
                  <p className="text-xs mt-1">This template has no personalization variables</p>
                </div>
              </div>
            ) : (
              <div className="h-full flex items-center justify-center text-center text-slate-400 dark:text-slate-500">
                <div>
                  <p className="text-sm font-medium">Select a Template</p>
                  <p className="text-xs mt-1">Variable mapping will appear here</p>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between bg-white dark:bg-gray-900">
          <button
            onClick={onBack}
            className="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-800 dark:hover:text-slate-200 transition-colors"
          >
            Back
          </button>
          <button
            onClick={handleContinue}
            disabled={!selectedTemplate}
            className="px-6 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Continue
          </button>
        </div>
      </div>

      {showTemplateCreator && (
        <TemplateCreator
          isOpen={showTemplateCreator}
          onClose={() => setShowTemplateCreator(false)}
          initialChannel="rcs"
          onSendRequest={() => {
            setShowTemplateCreator(false);
            getTemplates(undefined, { channel: "RCS" }).then(([response]) => {
              const raw = (response?.data ?? []).filter((t: any) => t?.is_active !== false);
              setTemplates(raw.map(convertToRcsTemplate));
            });
          }}
        />
      )}
    </div>
  );
};

export default RcsContentCreation;
